import type { JsonEditorProps } from "./json-editor.type";
import type { UnknownObject } from "@/types";
import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";

import { Editor } from "@monaco-editor/react";
import { Spin } from "antd";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import { useEffect, useRef, useState } from "react";

import { defineCustomTheme } from "./json-editor.helper";

import "./json-editor.style.less";

/**
 * JSON Editor component with a single Monaco editor.
 */
export const JsonEditor = <T extends UnknownObject | UnknownObject[]>({
  schema,
  onSchemaChange,
  isEditable = true,
  height,
  fullHeight,
}: JsonEditorProps<T>) => {
  // No longer need hasError state
  const [editorValue, setEditorValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const editorRef = useRef<editor.IStandaloneCodeEditor>(null);
  const monacoRef = useRef<Monaco>(null);
  const lastValidSchemaRef = useRef(schema);
  const userEditingRef = useRef(false);

  // Initialize editor with schema
  useEffect(() => {
    try {
      // Only update if we're not in the middle of user editing
      if (!userEditingRef.current) {
        // Store the valid schema for reference
        if (schema !== null && schema !== undefined) {
          lastValidSchemaRef.current = schema;
        }

        const formattedJson = JSON.stringify(schema, null, 2);
        setEditorValue(formattedJson);
        // JSON is valid
      }

      setIsLoading(false);
    } catch (_error) {
      console.error("Error formatting JSON:", _error);
      // JSON is invalid
      setIsLoading(false);
    }
  }, [schema]);

  // Handle editor mount
  const handleEditorDidMount = (
    editor: editor.IStandaloneCodeEditor,
    monaco: Monaco
  ) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Set up JSON validation
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: [],
    });

    // Define custom theme
    defineCustomTheme(monaco);
  };

  // Handle editor value change
  const handleEditorChange = (value: string | undefined) => {
    if (!value) return;

    // Set flag to indicate user is editing
    userEditingRef.current = true;

    // Always update the editor value, even if it has errors
    setEditorValue(value);

    try {
      const parsed = JSON.parse(value);
      lastValidSchemaRef.current = parsed;

      // Only update schema if it actually changed
      if (!isEqual(parsed, schema)) {
        onSchemaChange(parsed, true);
      }
    } catch (_error) {
      // Invalid JSON - prevent saving by setting isValid to false
      // CRITICAL: Never pass null to onSchemaChange
      // Instead, we keep the last valid schema and set isValid to false
      // This prevents the parent component from updating its state with invalid data
      if (
        lastValidSchemaRef.current !== null &&
        lastValidSchemaRef.current !== undefined
      ) {
        onSchemaChange(lastValidSchemaRef.current, false);
      }
    } finally {
      // Reset the flag after a short delay to allow for the schema update to complete
      setTimeout(() => {
        userEditingRef.current = false;
      }, 100);
    }
  };

  return (
    <div
      className={classNames("json-editor", {
        "-full-height": fullHeight,
      })}
    >
      {isLoading ? (
        <Spin />
      ) : (
        <Editor
          height={fullHeight ? "100%" : (height ?? "670px")}
          defaultLanguage="json"
          value={editorValue}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            readOnly: !isEditable,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            lineNumbers: "on",
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
            },
            automaticLayout: true,
            formatOnPaste: true,
            formatOnType: true,
            renderLineHighlight: "none",
            fontSize: 14,
            fontFamily:
              "'Monaco', 'Menlo', 'Consolas', 'Courier New', monospace",
            wordWrap: "on",
            folding: true,
            foldingStrategy: "indentation",
          }}
          theme="json-viewer-light"
        />
      )}
    </div>
  );
};
